import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, CheckCircle, TrashSimple, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API } from '@/config/api';

const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const KIND_ACCENT = {
  student_created: 'bg-emerald-500',
  student_updated: 'bg-blue-500',
  student_submitted: 'bg-indigo-500',
  document_uploaded: 'bg-sky-500',
  recruiter_signup: 'bg-purple-500',
  contact_message: 'bg-amber-500',
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/notifications?limit=25`);
      setItems(res.data?.items || []);
      setUnread(res.data?.unread_count || 0);
    } catch (err) { console.error('notifications load:', err); }
    finally { setLoading(false); }
  }, []);

  const pollUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/unread-count`);
      setUnread(res.data?.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { pollUnread(); const t = setInterval(pollUnread, 30000); return () => clearInterval(t); }, [pollUnread]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const onClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open, load]);

  const markRead = async (id) => {
    try { await axios.post(`${API}/admin/notifications/${id}/read`); setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n)); setUnread(u => Math.max(0, u - 1)); } catch { /* silent */ }
  };
  const markAllRead = async () => {
    try { await axios.post(`${API}/admin/notifications/mark-all-read`); setItems(prev => prev.map(n => ({ ...n, read: true }))); setUnread(0); toast.success('All notifications marked as read'); } catch { toast.error('Failed'); }
  };
  const dismiss = async (id) => {
    try { await axios.delete(`${API}/admin/notifications/${id}`); setItems(prev => prev.filter(n => n.id !== id)); pollUnread(); } catch { /* silent */ }
  };
  const openNotification = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="admin-notifications-wrapper">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
        aria-label="Notifications"
        data-testid="admin-notifications-bell"
      >
        <Bell size={20} weight={unread > 0 ? 'fill' : 'regular'} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center"
            data-testid="admin-notifications-unread-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-w-[92vw] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50" data-testid="admin-notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/60">
            <div>
              <p className="text-sm font-semibold text-gray-900">Notifications</p>
              <p className="text-[11px] text-gray-500">{unread} unread{items.length ? ` \u00b7 ${items.length} shown` : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-[11px] text-blue-700 hover:underline font-medium" data-testid="admin-notifications-mark-all-read">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700" aria-label="Close">
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            {loading && items.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-8">Loading…</p>
            )}
            {!loading && items.length === 0 && (
              <div className="py-10 text-center" data-testid="admin-notifications-empty">
                <Bell size={28} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No notifications yet</p>
                <p className="text-[11px] text-gray-400 mt-0.5">You&rsquo;ll be pinged when a recruiter adds a student, uploads a document, submits an application, or signs up.</p>
              </div>
            )}
            {items.map(n => (
              <div
                key={n.id}
                className={`group px-4 py-3 flex items-start gap-3 hover:bg-gray-50 cursor-pointer relative ${!n.read ? 'bg-blue-50/40' : ''}`}
                onClick={() => openNotification(n)}
                data-testid={`admin-notification-${n.id}`}
              >
                <span className={`shrink-0 mt-1.5 h-2 w-2 rounded-full ${KIND_ACCENT[n.kind] || 'bg-gray-400'}`} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${n.read ? 'text-gray-700' : 'text-gray-900 font-medium'} truncate`}>{n.title}</p>
                  {n.message && (<p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>)}
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400">
                    <span>{relTime(n.created_at)}</span>
                    {n.actor_email && (<><span>·</span><span className="truncate max-w-[180px]" title={n.actor_email}>{n.actor_email}</span></>)}
                  </div>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1 shrink-0">
                  {!n.read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                      className="p-1 text-gray-400 hover:text-emerald-600"
                      title="Mark as read"
                      data-testid={`admin-notification-mark-read-${n.id}`}
                    ><CheckCircle size={14} /></button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Dismiss"
                    data-testid={`admin-notification-dismiss-${n.id}`}
                  ><TrashSimple size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
